export const LIFESTYLE_RESTRICTION_SUB_FIELDS = [
  'meals_dietary_restrictions',
  'caffeine_alcohol_tobacco_other_restrictions',
  'physical_activity_restrictions',
  'other_activity_restrictions',
];

export const LIFESTYLE_SUBFIELD_ENABLE_MAP = {
  meals: 'meals_dietary_restrictions',
  caffeine: 'caffeine_alcohol_tobacco_other_restrictions',
  physical: 'physical_activity_restrictions',
  other: 'other_activity_restrictions',
};

export const PREP_HANDLING_SUB_FIELDS = [
  'preparation_of_investigational_trial_intervention',
  'storage_handling_of_investigational_trial_intervention',
  'accountability_of_investigational_trial_intervention',
];

export const PREP_SUBFIELD_ENABLE_MAP = {
  preparation: 'preparation_of_investigational_trial_intervention',
  storageHandling: 'storage_handling_of_investigational_trial_intervention',
  accountability: 'accountability_of_investigational_trial_intervention',
};

export const ANALYSIS_OBJECTIVE_FIELD_NAMES = [
  'analyses_primary_objective',
  'analyses_secondary_objective',
];

export const ANALYSIS_SUBFIELD_KEYS = [
  'handling_of_data',
  'handling_of_missing_data',
  'sensitivity_analysis',
  'supplementary_analysis',
];

export const ANALYSIS_SUBFIELD_ENABLE_MAP = {
  handling_of_data: 'handling_of_data',
  handling_of_missing_data: 'handling_of_missing_data',
  sensitivity_analysis: 'sensitivity_analysis',
  supplementary_analysis: 'supplementary_analysis',
};

export const RESCUE_SUBFIELD_ENABLE_MAP = {
  backgroundTrialIntervention: 'background_trial_intervention',
  otherNonInvestigationalIntervention: 'other_noninvestigational_trial_intervention',
};

export const RESCUE_SUBFIELD_KEYS = Object.values(RESCUE_SUBFIELD_ENABLE_MAP);

export const isEmptyIntakeCell = (v) =>
  v == null || (typeof v === 'string' && (v.trim() === '' || v.trim() === 'Not Provided'));

const cellText = (v) => (isEmptyIntakeCell(v) ? '' : String(v).trim());

const parseJsonString = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const emptyAnalysisObjectiveJson = () => ({
  summary: '',
  handling_of_data: '',
  handling_of_missing_data: '',
  sensitivity_analysis: '',
  supplementary_analysis: '',
});

const emptyRescueMedicationJson = () => ({
  rescue_medication: '',
  background_trial_intervention: '',
  other_noninvestigational_trial_intervention: '',
});

export const normalizeAnalysisObjectiveFieldValue = (raw) => {
  const parsed = parseJsonString(raw);
  if (!parsed) {
    if (isEmptyIntakeCell(raw)) return JSON.stringify(emptyAnalysisObjectiveJson());
    return JSON.stringify({
      ...emptyAnalysisObjectiveJson(),
      handling_of_data: cellText(raw),
    });
  }

  const subsAnyFilled = ANALYSIS_SUBFIELD_KEYS.some((k) => !isEmptyIntakeCell(parsed[k]));
  if (subsAnyFilled) {
    parsed.summary = '';
  } else if (!isEmptyIntakeCell(parsed.summary)) {
    parsed.handling_of_data = cellText(parsed.summary);
    parsed.summary = '';
  }

  ANALYSIS_SUBFIELD_KEYS.forEach((k) => {
    parsed[k] = cellText(parsed[k]);
  });
  parsed.summary = cellText(parsed.summary);

  return JSON.stringify(parsed);
};

export const normalizeRescueMedicationFieldValue = (raw) => {
  const parsed = parseJsonString(raw);
  if (!parsed) {
    if (isEmptyIntakeCell(raw)) return JSON.stringify(emptyRescueMedicationJson());
    return JSON.stringify({
      ...emptyRescueMedicationJson(),
      background_trial_intervention: cellText(raw),
    });
  }

  const subsAnyFilled = RESCUE_SUBFIELD_KEYS.some((k) => !isEmptyIntakeCell(parsed[k]));
  if (subsAnyFilled) {
    parsed.rescue_medication = '';
  } else if (!isEmptyIntakeCell(parsed.rescue_medication)) {
    parsed.background_trial_intervention = cellText(parsed.rescue_medication);
    parsed.rescue_medication = '';
  }

  parsed.rescue_medication = cellText(parsed.rescue_medication);
  RESCUE_SUBFIELD_KEYS.forEach((k) => {
    parsed[k] = cellText(parsed[k]);
  });

  return JSON.stringify(parsed);
};

export const normalizeLifestyleCohortFields = (cohort) => {
  if (!cohort || typeof cohort !== 'object') return;

  const subVals = LIFESTYLE_RESTRICTION_SUB_FIELDS.map((k) => cohort[k]);
  const subsAnyFilled = subVals.some((v) => !isEmptyIntakeCell(v));
  const mainFilled = !isEmptyIntakeCell(cohort.lifestyle_restrictions);

  if (subsAnyFilled) {
    cohort.lifestyle_restrictions = '';
    return;
  }

  if (mainFilled) {
    cohort.meals_dietary_restrictions = cellText(cohort.lifestyle_restrictions);
    cohort.lifestyle_restrictions = '';
  }
};

export const normalizePrepHandlingRegimenFields = (regimen, enabledSubfields = {}) => {
  if (!regimen || typeof regimen !== 'object') return;

  const parentKey = 'preparation_storage_handling_accountability';
  const subVals = PREP_HANDLING_SUB_FIELDS.map((k) => regimen[k]);
  const subsAnyFilled = subVals.some((v) => !isEmptyIntakeCell(v));
  const mainFilled = !isEmptyIntakeCell(regimen[parentKey]);

  if (subsAnyFilled) {
    regimen[parentKey] = '';
    return;
  }

  if (mainFilled && enabledSubfields?.preparation) {
    regimen.preparation_of_investigational_trial_intervention = cellText(regimen[parentKey]);
    regimen[parentKey] = '';
  }
};

const scanObjectSlots = (root, onSlot) => {
  if (!root || typeof root !== 'object') return;
  Object.values(root).forEach((slot) => {
    if (slot && typeof slot === 'object' && !Array.isArray(slot)) {
      onSlot(slot);
    }
  });
};

export const inferLifestyleSubfieldEnables = (studyParts) => {
  const result = { meals: false, caffeine: false, physical: false, other: false };
  if (!studyParts || typeof studyParts !== 'object') return result;

  Object.values(studyParts).forEach((part) => {
    scanObjectSlots(part, (cohort) => {
      Object.entries(LIFESTYLE_SUBFIELD_ENABLE_MAP).forEach(([enableKey, fieldKey]) => {
        if (!isEmptyIntakeCell(cohort[fieldKey])) {
          result[enableKey] = true;
        }
      });
    });
  });

  return result;
};

export const inferPrepHandlingSubfieldEnables = (treatmentTable) => {
  const result = { preparation: false, storageHandling: false, accountability: false };
  if (!treatmentTable || typeof treatmentTable !== 'object') return result;

  Object.values(treatmentTable).forEach((regimen) => {
    if (!regimen || typeof regimen !== 'object') return;
    Object.entries(PREP_SUBFIELD_ENABLE_MAP).forEach(([enableKey, fieldKey]) => {
      if (!isEmptyIntakeCell(regimen[fieldKey])) {
        result[enableKey] = true;
      }
    });
  });

  return result;
};

export const inferAnalysisSubfieldEnables = (studyParts, fieldName) => {
  const result = {
    handling_of_data: false,
    handling_of_missing_data: false,
    sensitivity_analysis: false,
    supplementary_analysis: false,
  };
  if (!studyParts || typeof studyParts !== 'object') return result;

  Object.values(studyParts).forEach((part) => {
    scanObjectSlots(part, (slot) => {
      const parsed = parseJsonString(slot?.[fieldName]);
      if (!parsed) return;
      Object.entries(ANALYSIS_SUBFIELD_ENABLE_MAP).forEach(([enableKey, jsonKey]) => {
        if (!isEmptyIntakeCell(parsed[jsonKey])) {
          result[enableKey] = true;
        }
      });
    });
  });

  return result;
};

export const inferPrimaryAnalysisSubfieldEnables = (studyParts) =>
  inferAnalysisSubfieldEnables(studyParts, 'analyses_primary_objective');

export const inferSecondaryAnalysisSubfieldEnables = (studyParts) =>
  inferAnalysisSubfieldEnables(studyParts, 'analyses_secondary_objective');

export const inferRescueSubfieldEnables = (treatmentTable) => {
  const result = {
    backgroundTrialIntervention: false,
    otherNonInvestigationalIntervention: false,
  };
  if (!treatmentTable || typeof treatmentTable !== 'object') return result;

  Object.values(treatmentTable).forEach((regimen) => {
    if (!regimen || typeof regimen !== 'object') return;
    const parsed = parseJsonString(regimen.rescue_medication);
    if (!parsed) return;
    Object.entries(RESCUE_SUBFIELD_ENABLE_MAP).forEach(([enableKey, jsonKey]) => {
      if (!isEmptyIntakeCell(parsed[jsonKey])) {
        result[enableKey] = true;
      }
    });
  });

  return result;
};

export const getLifestyleParentDisplayValue = (cohort) => {
  if (!cohort || typeof cohort !== 'object') return '';
  const subsAnyFilled = LIFESTYLE_RESTRICTION_SUB_FIELDS.some((k) => !isEmptyIntakeCell(cohort[k]));
  if (subsAnyFilled) return '';
  return cellText(cohort.lifestyle_restrictions);
};

/**
 * Live (non-trimming) variant of getLifestyleParentDisplayValue — for use as a controlled
 * TextField's `value` while the user is actively typing. `cellText()` trims, which is correct
 * for the one-time data-migration callers of the function above, but re-trimming the field's
 * own value on every render strips a just-typed trailing space back off immediately.
 */
export const getLifestyleParentDisplayValueLive = (cohort) => {
  if (!cohort || typeof cohort !== 'object') return '';
  const subsAnyFilled = LIFESTYLE_RESTRICTION_SUB_FIELDS.some((k) => !isEmptyIntakeCell(cohort[k]));
  if (subsAnyFilled) return '';
  return isEmptyIntakeCell(cohort.lifestyle_restrictions) ? '' : String(cohort.lifestyle_restrictions);
};

export const getPrepHandlingParentDisplayValue = (regimen) => {
  if (!regimen || typeof regimen !== 'object') return '';
  const subsAnyFilled = PREP_HANDLING_SUB_FIELDS.some((k) => !isEmptyIntakeCell(regimen[k]));
  if (subsAnyFilled) return '';
  return cellText(regimen.preparation_storage_handling_accountability);
};

/** Live (non-trimming) variant of getPrepHandlingParentDisplayValue — see the lifestyle
 * variant above for why. */
export const getPrepHandlingParentDisplayValueLive = (regimen) => {
  if (!regimen || typeof regimen !== 'object') return '';
  const subsAnyFilled = PREP_HANDLING_SUB_FIELDS.some((k) => !isEmptyIntakeCell(regimen[k]));
  if (subsAnyFilled) return '';
  const raw = regimen.preparation_storage_handling_accountability;
  return isEmptyIntakeCell(raw) ? '' : String(raw);
};

export const normalizeStudyPartsParentSubFields = (studyParts) => {
  if (!studyParts || typeof studyParts !== 'object') return;
  Object.values(studyParts).forEach((part) => {
    scanObjectSlots(part, (slot) => {
      normalizeLifestyleCohortFields(slot);
      ANALYSIS_OBJECTIVE_FIELD_NAMES.forEach((fieldName) => {
        if (Object.prototype.hasOwnProperty.call(slot, fieldName)) {
          slot[fieldName] = normalizeAnalysisObjectiveFieldValue(slot[fieldName]);
        }
      });
    });
  });
};

export const normalizeTreatmentTableParentSubFields = (treatmentTable, enabledSubfields = {}) => {
  if (!treatmentTable || typeof treatmentTable !== 'object') return;
  Object.values(treatmentTable).forEach((regimen) => {
    normalizePrepHandlingRegimenFields(regimen, enabledSubfields);
    if (Object.prototype.hasOwnProperty.call(regimen, 'rescue_medication')) {
      regimen.rescue_medication = normalizeRescueMedicationFieldValue(regimen.rescue_medication);
    }
  });
};
